import React, { useEffect, useState } from 'react';
import { clientPortalApi, ClientPortalProfileInput } from '../../services/api';
import { useClientPortalAuth } from '../../context/ClientPortalAuthContext';
import { useToast } from '../../components/Toast';
import { ClientPortalLicenseCount, ClientPortalProfile } from '../../types';
import './ClientPortal.css';

const EMPTY_PROFILE_FORM: ClientPortalProfileInput = {
  client_name: '',
  telephone: '',
  email: '',
  wilaya: '',
  ville: '',
};

function formatDateLabel(value?: string | null): string {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

const ClientPortalProfilePage: React.FC = () => {
  const { user, updateUser } = useClientPortalAuth();
  const { showToast } = useToast();
  const [profile, setProfile] = useState<ClientPortalProfile | null>(null);
  const [formData, setFormData] = useState<ClientPortalProfileInput>(EMPTY_PROFILE_FORM);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const profileResponse = await clientPortalApi.getProfile();
        const profileData = profileResponse.data as ClientPortalProfile;

        if (!mounted) {
          return;
        }

        setProfile(profileData);
        setFormData({
          client_name: profileData.client_name || '',
          telephone: profileData.telephone || '',
          email: profileData.email || '',
          wilaya: profileData.wilaya || '',
          ville: profileData.ville || '',
        });
      } catch (err: any) {
        if (mounted) {
          showToast(err.response?.data?.error || 'Chargement du profil impossible.', 'error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void fetchProfile();

    return () => {
      mounted = false;
    };
  }, [showToast]);

  const needsOnboarding = Boolean(profile?.must_change_password);
  const licenseCounts: ClientPortalLicenseCount[] = profile?.license_counts || [];

  const handleSaveProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingProfile(true);

    try {
      await clientPortalApi.updateProfile(formData);
      const nextProfile: ClientPortalProfile = {
        ...(profile as ClientPortalProfile),
        ...formData,
      };

      setProfile(nextProfile);
      if (user) {
        updateUser({
          ...user,
          ...formData,
        });
      }

      showToast('Profil client mis a jour avec succes.', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Mise a jour du profil impossible.', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      showToast('Veuillez remplir tous les champs du mot de passe.', 'warning');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast('La confirmation du mot de passe ne correspond pas.', 'warning');
      return;
    }

    setSavingPassword(true);
    try {
      await clientPortalApi.changePassword(passwordForm.oldPassword, passwordForm.newPassword);
      setPasswordForm({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setProfile((prev) => (prev ? { ...prev, must_change_password: false } : prev));
      if (user) {
        updateUser({
          ...user,
          must_change_password: false,
        });
      }

      showToast('Mot de passe modifie avec succes.', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Modification du mot de passe impossible.', 'error');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return <div className="loading">Chargement du profil client...</div>;
  }

  if (needsOnboarding) {
    return (
      <div className="client-portal-grid">
        <div className="client-portal-stack">
          <section className="client-portal-card">
            <div className="client-portal-card-header">
              <div>
                <h2>Votre profil</h2>
                <p>
                  Verifiez vos informations maintenant. L email doit etre renseigne avant
                  de pouvoir ouvrir des tickets support.
                </p>
              </div>
            </div>

            <div className="client-portal-warning">
              Tant que le mot de passe par defaut n a pas ete remplace, cette page reste en
              mode configuration initiale.
            </div>

            <form className="client-portal-form" onSubmit={handleSaveProfile}>
              <div className="form-group">
                <label>Denomination</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Telephone</label>
                <input
                  type="text"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Wilaya</label>
                <input
                  type="text"
                  value={formData.wilaya}
                  onChange={(e) => setFormData({ ...formData, wilaya: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Ville</label>
                <input
                  type="text"
                  value={formData.ville}
                  onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                />
              </div>

              <div className="client-portal-form-actions">
                <button type="submit" className="btn btn-primary" disabled={savingProfile}>
                  {savingProfile ? 'Enregistrement...' : 'Enregistrer le profil'}
                </button>
              </div>
            </form>
          </section>
        </div>

        <div className="client-portal-stack">
          <section className="client-portal-card">
            <h3 className="client-portal-section-title">Changer le mot de passe</h3>
            <div className="client-portal-warning">
              Remplacez le mot de passe par defaut <strong>1</strong> pour activer votre
              espace client.
            </div>

            <form className="client-portal-form" onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>Mot de passe actuel</label>
                <input
                  type="password"
                  value={passwordForm.oldPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, oldPassword: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label>Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                  }
                />
              </div>

              <div className="client-portal-form-actions">
                <button type="submit" className="btn btn-secondary" disabled={savingPassword}>
                  {savingPassword ? 'Mise a jour...' : 'Mettre a jour le mot de passe'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="client-portal-grid">
      <div className="client-portal-stack">
        <section className="client-portal-card">
          <div className="client-portal-card-header">
            <div>
              <h2>Etat du compte client</h2>
              <p>Le portail est reserve aux clients ayant un contrat actif non expire.</p>
            </div>
          </div>

          <div className="client-portal-metrics">
            <div className="client-portal-metric">
              <span className="client-portal-metric-label">Contrats actifs</span>
              <span className="client-portal-metric-value">
                {profile?.active_contract_count ?? 0}
              </span>
            </div>

            <div className="client-portal-metric">
              <span className="client-portal-metric-label">Prochaine echeance</span>
              <span className="client-portal-metric-value">
                {formatDateLabel(profile?.next_contract_expiry)}
              </span>
            </div>
          </div>

          <div className="client-portal-license-section">
            <h3 className="client-portal-section-title">Licences par logiciel</h3>
            {licenseCounts.length > 0 ? (
              <div className="client-portal-license-grid">
                {licenseCounts.map((license) => (
                  <div key={license.license_type} className="client-portal-license-card">
                    <span className="client-portal-license-name">{license.license_type}</span>
                    <strong className="client-portal-license-count">
                      {license.license_count}
                    </strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className="client-portal-empty">
                Aucune licence active non expiree pour le moment.
              </div>
            )}
          </div>
        </section>

        <section className="client-portal-card">
          <div className="client-portal-card-header">
            <div>
              <h3>Profil client</h3>
              <p>
                Corrigez vos informations ici. L email servira aux notifications support.
              </p>
            </div>
          </div>

          <form className="client-portal-form" onSubmit={handleSaveProfile}>
            <div className="form-group">
              <label>Denomination</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Telephone</label>
              <input
                type="text"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <small className="client-portal-field-help">
                Obligatoire pour recevoir les reponses support et ouvrir des tickets.
              </small>
            </div>

            <div className="form-group">
              <label>Wilaya</label>
              <input
                type="text"
                value={formData.wilaya}
                onChange={(e) => setFormData({ ...formData, wilaya: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Ville</label>
              <input
                type="text"
                value={formData.ville}
                onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
              />
            </div>

            <div className="client-portal-form-actions">
              <button type="submit" className="btn btn-primary" disabled={savingProfile}>
                {savingProfile ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </form>
        </section>
      </div>

      <div className="client-portal-stack">
        <section className="client-portal-card">
          <div className="client-portal-card-header">
            <div>
              <h3>Securite du compte</h3>
              <p>Modifiez votre mot de passe a tout moment depuis cette zone.</p>
            </div>
          </div>

          <form className="client-portal-form" onSubmit={handleChangePassword}>
            <div className="form-group">
              <label>Mot de passe actuel</label>
              <input
                type="password"
                value={passwordForm.oldPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, oldPassword: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Nouveau mot de passe</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Confirmer le nouveau mot de passe</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                }
              />
            </div>

            <div className="client-portal-form-actions">
              <button type="submit" className="btn btn-secondary" disabled={savingPassword}>
                {savingPassword ? 'Mise a jour...' : 'Changer le mot de passe'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ClientPortalProfilePage;
